import { useMemo, useCallback } from 'react';
import { useLocalStorage } from './useLocalStorage';
import { getDailyChallenge, getTodayUTC } from '../data/dailyChallenges';
import { questions } from '../data/questions';
import { DailyChallenge, LoginStreak } from '../types/game';

export function useDailyChallenge() {
  const today = getTodayUTC();

  const challenge = useMemo(() => getDailyChallenge(questions, today), [today]);

  const completedKey = `histocracy_daily_completed_${today}`;
  const [isCompleted, setIsCompleted] = useLocalStorage<boolean>(completedKey, false);

  const [loginStreak, setLoginStreak] = useLocalStorage<LoginStreak>(
    'histocracy_login_streak',
    { currentStreak: 0, longestStreak: 0, lastLoginDate: '' },
  );

  // Update login streak on mount / daily visit
  const updateLoginStreak = useCallback(() => {
    const yesterday = (() => {
      const d = new Date();
      d.setUTCDate(d.getUTCDate() - 1);
      const y = d.getUTCFullYear();
      const m = String(d.getUTCMonth() + 1).padStart(2, '0');
      const day = String(d.getUTCDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    })();

    setLoginStreak((prev) => {
      if (prev.lastLoginDate === today) return prev; // already counted today
      const continued = prev.lastLoginDate === yesterday;
      const newStreak = continued ? prev.currentStreak + 1 : 1;
      return {
        currentStreak: newStreak,
        longestStreak: Math.max(prev.longestStreak, newStreak),
        lastLoginDate: today,
      };
    });
  }, [today, setLoginStreak]);

  const markCompleted = useCallback(() => {
    setIsCompleted(true);
    updateLoginStreak();
  }, [setIsCompleted, updateLoginStreak]);

  return {
    challenge,
    isCompleted,
    markCompleted,
    loginStreak,
    today,
  };
}
